import { useState, useEffect } from "react";
import {
  FileText, Copy, Plus, Search, Trash2, Check,
  Sparkles, Tag, ExternalLink, Edit3, Clock, Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { toast } from "sonner";

const INITIAL_SNIPPETS = [
  {
    id: "sn-1",
    shortcut: "/review",
    name: "Google 5-Star Review Request",
    category: "Reputation",
    text: "Hi {{contact.first_name}}, thank you for partnering with Colibri! If you loved our service, could you take 30 seconds to drop us a quick 5-star review here: https://deck.weddingsthyzfoto.com/review ? It helps our team tremendously!",
    usageCount: 142
  },
  {
    id: "sn-2",
    shortcut: "/pricing",
    name: "Standard Agency Growth Packages",
    category: "Sales",
    text: "Hello {{contact.first_name}}, here is our full pricing breakdown: Starter ($1,497/mo), Growth ($2,997/mo), Enterprise Custom. Let me know if you would like to hop on a quick call to walk through the deliverables!",
    usageCount: 98
  },
  {
    id: "sn-3",
    shortcut: "/booking",
    name: "Direct Discovery Calendar",
    category: "Scheduling",
    text: "Feel free to grab any open slot on our calendar that fits your schedule: https://deck.weddingsthyzfoto.com/book/master",
    usageCount: 184
  },
  {
    id: "sn-4",
    shortcut: "/directions",
    name: "Studio Office Directions & Parking",
    category: "General",
    text: "We are located at 450 Lexington Ave, Suite 1400. Guest parking is validated in the North Garage. Take the high-rise elevators to the 14th floor reception.",
    usageCount: 45
  },
  {
    id: "sn-5",
    shortcut: "/deposit",
    name: "Retainer Deposit Link",
    category: "Finance",
    text: "Hi {{contact.first_name}}, to lock in your project kick-off date, please complete the secure retainer deposit here: https://deck.weddingsthyzfoto.com/pay/retainer",
    usageCount: 67
  }
];

export default function SnippetsTab() {
  const [snippets, setSnippets] = useState(() => {
    const saved = localStorage.getItem("ld_inbox_snippets");
    if (saved) {
      try { return JSON.parse(saved); } catch {}
    }
    return INITIAL_SNIPPETS;
  });

  useEffect(() => {
    localStorage.setItem("ld_inbox_snippets", JSON.stringify(snippets));
  }, [snippets]);

  const [categoryFilter, setCategoryFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);

  // New Snippet Form
  const [newSnippet, setNewSnippet] = useState({
    name: "",
    shortcut: "/",
    category: "General",
    text: ""
  });

  const handleCopy = (sn) => {
    navigator.clipboard.writeText(sn.text);
    setCopiedId(sn.id);
    setSnippets(prev =>
      prev.map(item => item.id === sn.id ? { ...item, usageCount: item.usageCount + 1 } : item)
    );
    toast.success(`Copied "${sn.shortcut}" to clipboard!`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = (id) => {
    setSnippets(prev => prev.filter(s => s.id !== id));
    toast.success("Snippet deleted");
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!newSnippet.name.trim() || !newSnippet.text.trim()) {
      toast.error("Please fill in snippet name and content");
      return;
    }
    let shortcut = newSnippet.shortcut.trim();
    if (!shortcut.startsWith("/")) shortcut = "/" + shortcut;

    const created = {
      id: "sn-" + Date.now(),
      name: newSnippet.name.trim(),
      shortcut,
      category: newSnippet.category,
      text: newSnippet.text.trim(),
      usageCount: 0
    };

    setSnippets(prev => [created, ...prev]);
    toast.success(`Snippet ${shortcut} created successfully!`);
    setIsAddOpen(false);
    setNewSnippet({
      name: "",
      shortcut: "/",
      category: "General",
      text: ""
    });
  };

  const insertToken = (token) => {
    setNewSnippet(prev => ({
      ...prev,
      text: prev.text + ` {{${token}}} `
    }));
  };

  const filtered = snippets.filter(s => {
    if (categoryFilter !== "all" && s.category !== categoryFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (
        s.name.toLowerCase().includes(q) ||
        s.shortcut.toLowerCase().includes(q) ||
        s.text.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const totalUses = snippets.reduce((acc, s) => acc + s.usageCount, 0);

  return (
    <div className="flex-1 h-full overflow-y-auto bg-slate-50 dark:bg-slate-950 p-6 font-sans">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Zap className="w-6 h-6 text-amber-500" />
              Snippets & Quick Replies
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Reusable canned responses and slash-command shortcuts to instantly reply in SMS, Email, and live chats.
            </p>
          </div>
          <Button
            onClick={() => setIsAddOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2 shadow-xs"
          >
            <Plus className="w-4 h-4" />
            + Create Snippet
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active Snippets</div>
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">{snippets.length}</div>
            <div className="text-xs text-blue-600 mt-1 font-medium">Ready for slash `/` insertion</div>
          </Card>
          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Dispatches</div>
            <div className="text-2xl font-bold text-emerald-600 mt-1">{totalUses}</div>
            <div className="text-xs text-slate-400 mt-1">Across SMS & Email threads</div>
          </Card>
          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Agent Time Saved</div>
            <div className="text-2xl font-bold text-purple-600 mt-1">~{Math.round(totalUses * 2.8 / 60)} hrs</div>
            <div className="text-xs text-slate-400 mt-1">Estimated ~2.8 min per message</div>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
            {["all", "Reputation", "Sales", "Scheduling", "General", "Finance"].map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  categoryFilter === cat
                    ? "bg-blue-600 text-white shadow-xs"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
                }`}
              >
                {cat === "all" ? "All Snippets" : cat}
              </button>
            ))}
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search shortcut or text..."
              className="pl-8 text-xs h-8 bg-slate-50 dark:bg-slate-950"
            />
          </div>
        </div>

        {/* Snippets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.length === 0 ? (
            <div className="col-span-2 text-center p-12 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
              <FileText className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No snippets found in this category.</p>
            </div>
          ) : (
            filtered.map((sn) => (
              <Card
                key={sn.id}
                className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-blue-400 transition-all flex flex-col justify-between shadow-xs"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                        {sn.shortcut}
                      </span>
                      <span className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate">
                        {sn.name}
                      </span>
                    </div>
                    <Badge variant="outline" className="text-[10px] text-slate-500 shrink-0">
                      {sn.category}
                    </Badge>
                  </div>

                  <p className="text-xs text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-950 p-3 rounded border border-slate-100 dark:border-slate-800 font-sans leading-relaxed break-words whitespace-pre-wrap">
                    {sn.text}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs">
                  <span className="text-[11px] text-slate-400">
                    Dispatched {sn.usageCount} times
                  </span>

                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleCopy(sn)}
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1 border-slate-300 hover:bg-blue-50 hover:text-blue-600"
                    >
                      {copiedId === sn.id ? (
                        <>
                          <Check className="w-3 h-3 text-emerald-600" />
                          <span className="text-emerald-600">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          Copy Text
                        </>
                      )}
                    </Button>
                    <button
                      onClick={() => handleDelete(sn.id)}
                      className="p-1.5 text-slate-400 hover:text-red-500 rounded transition-colors"
                      title="Delete snippet"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Add Snippet Modal */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="sm:max-w-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-base text-slate-900 dark:text-slate-100">Create New Snippet</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Create a reusable response that agents can instantly insert via slash command.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreate} className="space-y-3.5 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Snippet Name *</label>
                <Input
                  required
                  placeholder="e.g. Booking Link Dispatch"
                  value={newSnippet.name}
                  onChange={(e) => setNewSnippet({ ...newSnippet, name: e.target.value })}
                  className="text-xs mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Shortcut Command *</label>
                <Input
                  required
                  placeholder="/booking"
                  value={newSnippet.shortcut}
                  onChange={(e) => setNewSnippet({ ...newSnippet, shortcut: e.target.value })}
                  className="text-xs font-mono mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Category</label>
              <select
                value={newSnippet.category}
                onChange={(e) => setNewSnippet({ ...newSnippet, category: e.target.value })}
                className="w-full mt-1 h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-xs dark:bg-slate-950 dark:border-slate-800"
              >
                <option value="General">General</option>
                <option value="Reputation">Reputation</option>
                <option value="Sales">Sales</option>
                <option value="Scheduling">Scheduling</option>
                <option value="Finance">Finance</option>
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Message Content *</label>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-slate-400">Insert tag:</span>
                  <button
                    type="button"
                    onClick={() => insertToken("contact.first_name")}
                    className="text-[10px] bg-slate-100 hover:bg-slate-200 px-1.5 py-0.5 rounded text-blue-600"
                  >
                    + First Name
                  </button>
                  <button
                    type="button"
                    onClick={() => insertToken("booking_link")}
                    className="text-[10px] bg-slate-100 hover:bg-slate-200 px-1.5 py-0.5 rounded text-blue-600"
                  >
                    + Booking Link
                  </button>
                </div>
              </div>
              <Textarea
                required
                placeholder="Type your pre-written response..."
                value={newSnippet.text}
                onChange={(e) => setNewSnippet({ ...newSnippet, text: e.target.value })}
                className="text-xs min-h-[110px]"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setIsAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                Save Snippet
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

